import React, { useState, useEffect, useRef } from "react";

import { Box } from "@mui/material";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiSend } from "react-icons/fi";

import { useAppState } from "../../../context/StateContext";
import useActionComment from "../../../hooks/useActionComment";
import { Comment } from "../../../models/comment";
import AutoSuggest from "../../Form/AutoSuggest";
import Button, { Color } from "../../Form/Button";
import Input from "../../Form/Input";
import { LeftMessage, RightMessage } from "./Message";
import SkeletonCommunication from "./skeleton";
import useStyles from "./styles";

interface CommunicationProps {
  originId?: number;
  originTable?: string;
  secondaryOriginId?: number;
  viewOnly?: boolean;
  header?: React.ReactChild;
  sendComment?: (coment: Comment) => void;
  messages?: Comment[];
  dateFormat?: string;
  loading?: boolean;
}

const Communication = ({
  originId,
  originTable,
  secondaryOriginId,
  viewOnly = false,
  header,
  sendComment,
  messages,
  dateFormat,
  loading = false,
}: CommunicationProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentUser } = useAppState();

  const [users, setUsers] = useState<string[]>([]);
  const [comments, setComments] = useState<Comment[]>();
  const [message, setMessage] = useState<string>("");

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { getAllComments, useCreateComment } = useActionComment();

  const {
    data: commentsData,
    isFetching: loadingComments,
    refetch: refetchComments,
  } = getAllComments(
    [["createdAt", "DESC"]],
    originId,
    originTable,
    undefined,
    undefined,
    secondaryOriginId
  );

  useEffect(() => {
    if (messages?.length) {
      setComments(messages);
    }
    if (commentsData?.rows?.length) {
      setComments(commentsData.rows);
    }
  }, [messages, commentsData]);

  const { mutate: createComment, isLoading: loadingCreateComment } = useCreateComment(
    false,
    refetchComments
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        block: "end",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [comments]);

  useEffect(() => {
    if (comments) {
      const uniqueNames = Array.from(
        new Set(comments?.map((comment: Comment) => comment.user && comment.user.name))
      ) as string[];

      if (uniqueNames) setUsers(uniqueNames);
    }
  }, [comments]);

  const sendNewComment = () => {
    if (!currentUser || !currentUser.id) return;

    if (sendComment) {
      sendComment({
        userId: currentUser.id,
        message,
        createdAt: new Date().toISOString(),
        identifier: `${currentUser.name}${currentUser?.lastName ? ` ${currentUser.lastName}` : ""}`,
      });
    } else {
      createComment({
        userId: currentUser.id,
        message,
        originId,
        secondaryOriginId: undefined,
        originTable,
      } as Comment);
    }

    setMessage("");
  };

  const renderMessages = () => {
    if (loadingComments || loadingCreateComment || loading) {
      return (
        <div className={classes.messagesScroll}>
          <SkeletonCommunication />
        </div>
      );
    }

    if (!currentUser || !currentUser.id) return;
    if (!comments) return;

    if (comments.length === 0) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center">
          <span>{t("noCommentsBeTheFirstToComment")}</span>
        </Box>
      );
    }

    return (
      <>
        <hr className={classes.lineSeparator} />
        <div className={classes.messagesScroll}>
          <Stack
            data-cy="stack-messages-communication"
            direction="column"
            width="100%"
            height="100%"
            maxHeight="100%"
            justifyContent="flex-start"
            alignItems="center"
            spacing={3}
          >
            {comments.map((comment: Comment) => {
              if (currentUser.id === comment.userId)
                return <RightMessage key={comment.id} dateFormat={dateFormat} message={comment} />;

              return <LeftMessage key={comment.id} dateFormat={dateFormat} message={comment} />;
            })}
            <div ref={messagesEndRef}></div>
          </Stack>
        </div>
      </>
    );
  };

  const disableSendButton = (): boolean => {
    if (viewOnly) return true;
    if (!message) return true;
    if (message.length < 1) return true;
    if (!currentUser || !currentUser.id) return true;

    return false;
  };

  return (
    <Box
      className={classes.mainDiv}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {header ? (
        <>{header}</>
      ) : (
        <AutoSuggest
          data-cy="users-communication"
          value={users}
          label={t("subscribed")}
          multiple
          freeSolo
          disabled
          // rightIcon={<AiOutlineSearch />}
          getOptionLabel={(option: string) => option}
        />
      )}
      {renderMessages()}
      <Stack justifyContent="space-between" width="100%" direction="row" spacing={2}>
        <Input
          data-cy="message-input-communication"
          value={message}
          fullWidth
          placeholder={t("inputMessage")}
          onChange={(event) => setMessage(event.target.value.trimStart())}
          onKeyDown={(event) => {
            if (event.key && event.key.toLowerCase().trim() === "enter" && !disableSendButton()) {
              sendNewComment();
            }
          }}
        />
        <Button
          data-cy="send-message-communication"
          onClick={sendNewComment}
          className={classes.sendButton}
          customColor={Color.SECONDARY}
          disabled={disableSendButton()}
        >
          <Stack
            alignItems="center"
            justifyContent="center"
            width="100%"
            direction="row"
            spacing={1}
          >
            <span>{t("send")}</span>
            <FiSend size={14} />
          </Stack>
        </Button>
      </Stack>
    </Box>
  );
};

export default Communication;
